<script lang="ts">
  import type { Models } from '@smartlinkdev/types';
  import { goto } from '$app/navigation';
  import Avatar from '$lib/components/Avatar.svelte';
  import {
    currentLocation,
    mapFloors,
    mapInstance,
    venue,
    currentFloor,
    floors,
    roveActiveFloor,
    mapType,
  } from '$lib/stores';
  import { searchAndCategoryFiltered } from '$lib/stores/search';
  import { t } from '$lib/translations';

  const locationTextColor = $venue.settings?.embed?.theme?.sidebarTextColor || '#ccc';
  const locationHighlightTextColor = $venue.settings?.embed?.theme?.sidebarTextSelectedColor || '#fff';
  const highlightColor = $venue.settings?.embed?.theme?.highlightColor || '#c00';

  const clickLocation = (locationId: number): void => {
    $mapType === 'rove' ? changeCurrentFloor(locationId) : changeCurrentMapboxFloor(locationId)
    goto(`/venues/${$venue.id}/locations/${locationId}`);
  };

  const changeCurrentFloor = (locationId: number): void => {
    if ($floors && $mapInstance) {
      let newFloor = $floors.find(floor => floor.id === $mapInstance.getLocationFloor(locationId)[0]);
      roveActiveFloor.set(newFloor);
      currentFloor.set(newFloor)
      $mapInstance.changeFloor(newFloor);
    } else {
      console.log('$floors or $mapInstance undefined!');
    }
  }

  const changeCurrentMapboxFloor = (locationId: number): void => {
    let newFloor = $mapFloors.find((floor) => floor.name === locationFloor(locationId));
    if(newFloor) currentFloor.set(newFloor);
  }

  const isNumber = (numStr: string): boolean => {
    return !isNaN(+numStr);
  };

  const locationInitials = (name: string): string => {
    if (name.trim().length < 1) return '';
    const pieces = name.trim().split(' ');
    const firstWord = pieces[0];
    if (isNumber(firstWord)) {
      return firstWord;
    }
    return Array.from(name.trim())[0];
  };

  const locationFloor = (locationId: number): string => {
    if (!$mapInstance) {
      return '';
    }
    const floorIndexes = $mapInstance.getLocationFloor(locationId);
    if (floorIndexes[0] === -1) {
      return '';
    }
    const floorNames: string[] = [];
    floorIndexes.forEach((floorIndex) => {
      const found = $mapFloors.find((mf) => mf.index === floorIndex);
      if (found) {
        floorNames.push(found.name);
      }
    });
    return floorNames.join(' | ');
  };

  const isComingSoon = (location: Models.Location): boolean => {
    return location.location_types?.some(
      (type: Models.LocationType) =>
        type.highlight &&
        type.name.toLowerCase() === 'Coming Soon'.toLowerCase()
    );
  };
</script>

<div class="location-listing" style={`color: ${locationTextColor}`}>
  {#each $searchAndCategoryFiltered as location (location.id)}
    <div
      class="location-listing-item"
      style={$currentLocation && $currentLocation.id === location.id ? `background-color: ${highlightColor}` : ''}
      on:click={() => {
        clickLocation(location.id);
      }}
      on:keydown={() => {
        // TODO
      }}
    >
      {#if location.logo_img}
        <img
          class="location-img"
          src={location.logo_img}
          alt={`${location.name} logo`}
        />
      {:else}
        <Avatar text={locationInitials(location.name)} />
      {/if}
      <div class="location-name-wrapper">
        <div
          class="location-name"
          style={$currentLocation && $currentLocation.id === location.id
            ? `color: ${locationHighlightTextColor}`
            : ''}
        >
          {location.name}
        </div>
        <div class="location-subtitle">
          {#if isComingSoon(location)}
            { $t('content.comingSoon') }
          {:else}
            {locationFloor(location.id)}
          {/if}
        </div>
      </div>
    </div>
  {/each}
</div>

<style lang="postcss">
  .location-listing {
    @apply overflow-y-auto bg-white h-[85%] mt-4;
  }

  .location-listing-item {
    @apply h-[2.25rem] text-xs flex my-1 text-gray-800 items-center pl-2;
    font-weight: 500;
  }

  .location-listing-item:hover {
    @apply font-bold cursor-pointer;
  }

  .location-img {
    @apply object-contain h-[2.25rem] w-[2.5rem] overflow-hidden p-0;
  }

  .location-name-wrapper {
    @apply w-full ml-2;
  }

  .location-subtitle {
    color: #adb5bd;
  }

</style>
